import React, { useEffect, useState } from 'react';
import ReactFlow, {
  Controls,
  Background,
  Handle,
  Position,
  useNodesState,
  useEdgesState
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Button, Space, Popconfirm, Tooltip } from 'antd';
import {
  GlobalOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';

/**
 * 🏢 Custom CMT Node Component for React-Flow
 * WITH INTERACTIVE EDIT CONTROLS FOR ADMIN MODE
 */
const CMTNode = ({ data }) => {
  const { showEditControls, onEdit, onDelete, onAddChild } = data;
  const getNodeStyle = (type) => {
    const baseStyle = {
      border: '2px solid #1890ff',
      borderRadius: '12px',
      padding: '24px',
      backgroundColor: '#f8fbff',
      minWidth: '280px',
      maxWidth: '350px',
      boxShadow: '0 4px 16px rgba(24,144,255,0.15)',
      textAlign: 'center',
      position: 'relative'
    };

    // Type-specific styling
    const typeStyles = {
      Global: {
        borderColor: '#722ed1',
        backgroundColor: '#f9f0ff',
        boxShadow: '0 6px 20px rgba(114,46,209,0.2)'
      },
      Regional: {
        borderColor: '#1890ff',
        backgroundColor: '#f0f8ff'
      },
      Country: {
        borderColor: '#52c41a',
        backgroundColor: '#f6ffed',
        boxShadow: '0 4px 16px rgba(82,196,26,0.15)'
      },
      Site: {
        borderColor: '#fa8c16',
        backgroundColor: '#fff7e6',
        boxShadow: '0 4px 16px rgba(250,140,22,0.15)'
      }
    };

    return { ...baseStyle, ...typeStyles[type] };
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      Global: <GlobalOutlined style={{ fontSize: '16px', marginRight: '6px' }} />,
      Regional: <ApartmentOutlined style={{ fontSize: '16px', marginRight: '6px' }} />,
      Country: <EnvironmentOutlined style={{ fontSize: '16px', marginRight: '6px' }} />,
      Site: <TeamOutlined style={{ fontSize: '16px', marginRight: '6px' }} />
    };
    return iconMap[type] || null;
  };

  return (
    <div style={getNodeStyle(data.type)}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#1890ff',
          width: '12px',
          height: '12px',
          border: '2px solid #fff'
        }}
      />
      
      <div style={{ 
        fontWeight: 'bold', 
        fontSize: '20px', 
        marginBottom: '8px',
        color: '#1890ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {getTypeIcon(data.type)}
        {data.name}
      </div>
      
      <div style={{ 
        fontSize: '14px', 
        color: '#8c8c8c', 
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: '600'
      }}>
        {data.type} CMT
      </div>
      
      <div style={{ 
        fontSize: '15px', 
        fontStyle: 'italic', 
        lineHeight: '1.4',
        color: '#595959'
      }}>
        {data.scope}
      </div>

      {/* 🚨 INTERACTIVE EDIT CONTROLS */}
      {showEditControls && (
        <div style={{ marginTop: '16px', borderTop: '1px solid #e8e8e8', paddingTop: '12px' }}>
          <Space size="small">
            <Tooltip title="Edit CMT">
              <Button 
                type="text" 
                size="small" 
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  // 🧹 Pass clean CMT data only (no React Flow functions)
                  onEdit && onEdit({
                    id: data.id,
                    name: data.name,
                    type: data.type,
                    scope: data.scope
                  });
                }}
              />
            </Tooltip>
            
            <Tooltip title="Add Child CMT">
              <Button 
                type="text" 
                size="small" 
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  // 🧹 Pass clean parent ID only (no React Flow functions)
                  onAddChild && onAddChild(data.id);
                }}
              />
            </Tooltip>
            
            <Tooltip title="Delete CMT">
              <Popconfirm
                title="Delete CMT"
                description={`Are you sure you want to delete ${data.name}?`}
                onConfirm={() => onDelete && onDelete(data.id)}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
              >
                <Button 
                  type="text" 
                  size="small" 
                  icon={<DeleteOutlined />}
                  danger
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#1890ff',
          width: '12px',
          height: '12px',
          border: '2px solid #fff'
        }}
      />
    </div>
  );
};

// Register the custom node type
const nodeTypes = {
  cmtNode: CMTNode
};

/**
 * 🔄 Convert Hierarchy Tree to React-Flow Nodes and Edges
 * WITH EDIT CONTROLS SUPPORT
 */
const convertHierarchyToFlow = (rootNode, showEditControls = false, onNodeEdit, onNodeDelete, onNodeAddChild) => {
  const nodes = [];
  const edges = [];
  
  // Recursively process nodes
  const processNode = (node, parentId = null) => {
    // Generate unique ID if not present
    const currentNodeId = node.id || `${node.type}_${node.name.replace(/\s+/g, '_').toLowerCase()}_${nodes.length}`;
    
    // Create node - 🧹 ENSURE CLEAN DATA (no function contamination)
    nodes.push({
      id: currentNodeId,
      type: 'cmtNode',
      data: {
        id: node.id || currentNodeId, // 🧹 Store clean ID for callbacks
        name: node.name,
        type: node.type,
        scope: node.scope,
        showEditControls: showEditControls,
        onEdit: onNodeEdit,
        onDelete: onNodeDelete,
        onAddChild: onNodeAddChild
      },
      position: { x: 0, y: 0 } // Will be calculated by dagre
    });
    
    // Create edge from parent if exists
    if (parentId) {
      edges.push({
        id: `edge_${parentId}_${currentNodeId}`,
        source: parentId,
        target: currentNodeId,
        type: 'default',
        style: {
          stroke: '#1890ff',
          strokeWidth: 2
        },
        markerEnd: {
          type: 'arrowclosed',
          color: '#1890ff'
        }
      });
    }
    
    // Process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        processNode(child, currentNodeId);
      });
    }
  };
  
  processNode(rootNode);
  return { nodes, edges };
};

/**
 * 📐 Apply Dagre Layout Algorithm for Professional Positioning
 * IDENTICAL to MultiCMTConfiguration version
 */
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Configure layout - IDENTICAL spacing
  dagreGraph.setGraph({ 
    rankdir: direction,
    ranksep: 300,
    nodesep: 200,
    edgesep: 80,
    marginx: 100,
    marginy: 100
  });

  // Add nodes to graph - IDENTICAL dimensions
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { 
      width: 350,
      height: 180
    });
  });

  // Add edges to graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWithPosition.width / 2,
        y: nodeWithPosition.y - nodeWithPosition.height / 2
      }
    };
  });

  return { nodes: layoutedNodes, edges };
};

/**
 * 🎯 CMT Structure Visualization Component
 * 
 * Reusable component for displaying CMT organizational charts
 * Used in both onboarding wizard and admin view
 * 
 * @param {Object} hierarchyData - CMT hierarchy tree structure
 * @param {boolean} readOnly - Whether to show edit controls (future)
 * @param {function} onStructureChange - Callback for edits (future)
 * @param {boolean} showEditControls - Show edit UI (future)
 */
const CMTStructureVisualization = ({ 
  hierarchyData, 
  readOnly = true, 
  onStructureChange = null,
  showEditControls = false,
  onNodeEdit,
  onNodeDelete,
  onNodeAddChild
}) => {
  console.log('🎨 CMTStructureVisualization received hierarchyData:', JSON.stringify(hierarchyData, null, 2));
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);
  const [rfInstance, setRfInstance] = useState(null);

  // Update React-Flow when hierarchy data changes
  useEffect(() => {
    if (hierarchyData && hierarchyData.root) {
      console.log('🔄 Converting hierarchy to React Flow...');
      const { nodes: newNodes, edges: newEdges } = convertHierarchyToFlow(
        hierarchyData.root, 
        showEditControls, 
        onNodeEdit, 
        onNodeDelete, 
        onNodeAddChild
      );
      console.log('🔄 Generated nodes:', newNodes.length, newNodes);
      console.log('🔄 Generated edges:', newEdges.length, newEdges);
      
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(newNodes, newEdges);
      console.log('🔄 Final layouted nodes:', layoutedNodes.length, layoutedNodes);
      console.log('🔄 Final layouted edges:', layoutedEdges.length, layoutedEdges);
      
      setFlowNodes(layoutedNodes);
      setFlowEdges(layoutedEdges);
    }
  }, [hierarchyData, setFlowNodes, setFlowEdges]);

  // No data state
  if (!hierarchyData || !hierarchyData.root) {
    return (
      <div style={{
        width: '100%',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        color: '#999'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ApartmentOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <div style={{ fontSize: '16px' }}>No CMT structure configured</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        width: '100%',
        height: '800px',  // IDENTICAL container height
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#fafafa'
      }}
    >
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        nodeTypes={nodeTypes}
        onInit={setRfInstance}
        fitView={true}
        fitViewOptions={{
          padding: 30,
          includeHiddenNodes: false,
          minZoom: 0.4,
          maxZoom: 2.0
        }}
        minZoom={0.2}
        maxZoom={3.0}
        style={{
          backgroundColor: '#f9f9f9'
        }}
        proOptions={{
          hideAttribution: true
        }}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
      >
        <Background 
          variant="dots" 
          gap={20} 
          size={1}
          color="#e8e8e8"
        />
        <Controls 
          style={{
            button: {
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              color: '#595959'
            }
          }}
        />
      </ReactFlow>
    </div>
  );
};

export default CMTStructureVisualization;